import React, { useMemo, useState } from "react";

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTime(value) {
  const d = toDate(value);
  if (!d) return "Just now";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDate(value) {
  const d = toDate(value);
  if (!d) return "Recent activity";
  return d.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function toneForType(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("winner") || t.includes("complete")) {
    return "border-green-500/30 bg-green-500/10 text-green-300";
  }
  if (t.includes("elimin")) {
    return "border-red-500/30 bg-red-500/10 text-red-300";
  }
  if (t.includes("rebuy") || t.includes("add_on") || t.includes("dealer")) {
    return "border-amber-500/30 bg-amber-500/10 text-amber-300";
  }
  if (t.includes("clock") || t.includes("level") || t.includes("break")) {
    return "border-blue-500/30 bg-blue-500/10 text-blue-300";
  }
  return "border-zinc-700 bg-zinc-800/70 text-zinc-300";
}

export default function TournamentTimelineCard({ events, loading = false }) {
  const [expanded, setExpanded] = useState(false);

  const visibleEvents = useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    return expanded ? list : list.slice(0, 8);
  }, [events, expanded]);

  return (
    <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-xl font-semibold text-[#B87333]">Tournament Timeline</h3>
          <p className="text-zinc-400 text-sm mt-1">
            A permanent record of clock changes, player actions, seating, and results.
          </p>
        </div>

        <span className="inline-flex items-center rounded-full border border-zinc-700 bg-zinc-800 px-2.5 py-1 text-xs font-semibold text-zinc-300">
          {Array.isArray(events) ? events.length : 0} Events
        </span>
      </div>

      {loading ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 text-zinc-400">
          Loading tournament activity...
        </div>
      ) : visibleEvents.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-700 bg-zinc-950 p-5 text-center">
          <p className="text-zinc-300 font-semibold">No activity recorded yet</p>
          <p className="text-zinc-500 text-sm mt-1">
            New clock and tournament actions will appear here automatically.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleEvents.map((event) => (
            <div
              key={event.id}
              className="rounded-lg border border-zinc-800 bg-zinc-950 p-4"
            >
              <div className="flex items-start gap-3">
                <div className="min-w-[72px] text-right">
                  <div className="text-zinc-200 text-sm font-semibold">
                    {formatTime(event.createdAt)}
                  </div>
                  <div className="text-zinc-600 text-xs mt-0.5">
                    {formatDate(event.createdAt)}
                  </div>
                </div>

                <div className="w-px self-stretch bg-zinc-800" />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${toneForType(
                        event.type
                      )}`}
                    >
                      {String(event.type || "activity").replaceAll("_", " ")}
                    </span>
                    <p className="text-zinc-100 font-semibold">{event.title}</p>
                  </div>

                  {event.message ? (
                    <p className="text-zinc-400 text-sm mt-1 leading-relaxed">
                      {event.message}
                    </p>
                  ) : null}

                  {event.actorName ? (
                    <p className="text-zinc-600 text-xs mt-2">Recorded by {event.actorName}</p>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {Array.isArray(events) && events.length > 8 ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-3 text-zinc-200 font-semibold hover:bg-zinc-700"
        >
          {expanded ? "Show Recent Events" : `Show All ${events.length} Events`}
        </button>
      ) : null}
    </div>
  );
}
